// Accessibility issues fixed by AI
/* eslint-disable valid-jsdoc */
import React, { PureComponent } from 'react';
import _ from 'underscore';
import { keyCode } from '@bingads-webui/keycode';
import {
  WithArrowKeys,
  IWithArrowKeysInjectedProps,
  IWithArrowKeysOptions,
} from '@bingads-webui-react/hoc-with-arrow-keys';
import { ListItem, ListItemWithToolTip } from './list-item';
import { AriaRoleType, ItemAriaRoleConfig } from './aria-role-config';
import { IRawListItemItem, IRawListSelectorProps } from './types';

interface IRawListSelectorState {
  focusedIndex: number;
}

/**
 * A list component contains a collection of items,
 */
class RawListSelector extends PureComponent<IRawListSelectorProps & IWithArrowKeysInjectedProps, IRawListSelectorState> {
  static defaultProps: Partial<IRawListSelectorProps & IWithArrowKeysInjectedProps> = {
    renderHeader: undefined,
    renderFooter: undefined,
    renderDivider: undefined,
    renderEmptyList: undefined,
    renderActionBar: undefined,
    keyExtractor: (item, index) => item.Id || index,
    typeExtractor: item => item.type,
    extraData: undefined,
    onSelect: _.noop,
    selectedItem: undefined,
    checkSelected: (selectedItem, item) => _.isEqual(selectedItem, item),
    enableArrowKeysSupport: false,
    // WithArrowKeys always replaces this legacy no-op default with its observable.
    observable: _.noop as unknown as IWithArrowKeysInjectedProps['observable'],
    onKeyDown: _.noop,
    onBlur: _.noop,
    ariaLabel: '',
    ariaRole: AriaRoleType.ListBox,
    enableOverflowTooltip: false,
  };

  constructor(props: IRawListSelectorProps & IWithArrowKeysInjectedProps) {
    super(props);

    const {
      enableArrowKeysSupport,
      observable,
    } = props;

    let observer: { next: (data: number) => void } = { next: _.noop };
    if (enableArrowKeysSupport) {
      observer = {
        next: (data: number) => {
          switch (data) {
            case keyCode.ArrowUp:
              this.setState((preState, prevProps) => ({
                focusedIndex: this.getNextFocusedItemIndex(preState.focusedIndex, prevProps, false),
              }));
              break;
            case keyCode.ArrowDown:
              this.setState((preState, prevProps) => ({
                focusedIndex: this.getNextFocusedItemIndex(preState.focusedIndex, prevProps, true),
              }));
              break;
            case keyCode.Tab:
              this.setState({ focusedIndex: -1 });
              break;
            default:
              break;
          }
        },
      };
    }

    observable.subscribe(observer);

    this.state = { focusedIndex: -1 };
  }

  onBlur = () => {
    this.setState({ focusedIndex: -1 });
    this.props.onBlur();
  };

  getNextFocusedItemIndex(previousIndex: number, props: IRawListSelectorProps, isNext: boolean): number {
    const { data, typeExtractor } = props;
    const maxIndex = data.length - 1;

    let newIndex: number;

    if (previousIndex < 0) {
      newIndex = 0;
    } else if (previousIndex > maxIndex) {
      newIndex = maxIndex;
    } else if (isNext && previousIndex === maxIndex) {
      newIndex = 0;
    } else if (!isNext && previousIndex === 0) {
      newIndex = maxIndex;
    } else {
      newIndex = isNext ? previousIndex + 1 : previousIndex - 1;
    }

    if (typeExtractor(data[newIndex]) === 'divider') {
      return this.getNextFocusedItemIndex(newIndex, props, isNext);
    }

    return newIndex;
  }

  focusItemCallback = (index: number) => {
    this.setState({ focusedIndex: index });
  };

  render() {
    const {
      renderHeader,
      renderFooter,
      renderEmptyList,
      renderItem,
      renderActionBar,
      keyExtractor,
      renderDivider,
      ariaLabel,
      ariaRole,
      data,
      enableOverflowTooltip,
      onKeyDown,
    } = this.props;

    const ListItemClass = enableOverflowTooltip ? ListItemWithToolTip : ListItem;
    const validItemsCount = _.size(_.filter(this.props.data, item => this.props.typeExtractor(item) !== 'divider'));
    let positionInValidItems = 0;

    // this false alarm rule requires a role on div but doesn't recognize dynamic role
    /* eslint-disable jsx-a11y/no-static-element-interactions */
    return (
      <div className="ba-list-selector">
        {renderHeader && (
          <div className="list-selector-header">{renderHeader()}</div>
        )}

        {data.length > 0 && renderActionBar && (
          <div className="list-selector-action-bar" role="menu">{renderActionBar({ itemCount: data.length })}</div>
        )}

        <div
          className="list-selector-content"
          role={ariaRole}
          onKeyDown={event => onKeyDown?.(event)}
          onBlur={this.onBlur}
          aria-label={ariaLabel || 'selected result'}
        >
          {_.map(this.props.data, (item, index) => {
            const type = this.props.typeExtractor(item);
            const key = keyExtractor(item, index);

            switch (type) {
              case 'divider':
                return renderDivider ? (
                  renderDivider(item, index, key)
                ) : (
                  <hr key={key} />
                );
              default:
                positionInValidItems += 1;
                return (
                  <ListItemClass
                    ariaRole={ItemAriaRoleConfig[ariaRole!]}
                    listSize={validItemsCount}
                    positionInList={positionInValidItems}
                    item={item}
                    index={index}
                    key={key}
                    keyValue={key}
                    onSelect={this.props.onSelect}
                    checkSelected={this.props.checkSelected}
                    selectedItem={this.props.selectedItem}
                    renderItem={renderItem}
                    isFocused={index === this.state.focusedIndex}
                    focusItemCallback={this.focusItemCallback}
                  />
                );
            }
          })}

          {this.props.data.length === 0 && renderEmptyList && (
            <div className="list-selector-empty">{renderEmptyList()}</div>
          )}
        </div>
        {renderFooter && (
          <div className="list-selector-footer">{renderFooter()}</div>
        )}
      </div>
    );
  }
}

export interface IListSelectorComponent {
  <TItem extends object = IRawListItemItem>(
    props: IRawListSelectorProps<TItem> & IWithArrowKeysOptions
  ): React.ReactElement | null;
}

export const ListSelector = WithArrowKeys(
  RawListSelector,
  { interceptTabKey: false }
) as IListSelectorComponent;
